import { Inbox } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router';
import {
  Card,
  DataTable,
  EmptyState,
  Tabs,
  type TabItem,
} from '@/components/ui';
import { useSession } from '@/features/auth/session-context';
import { useBranch } from '@/features/branch/branch-context';
import { useDoctorToday } from '@/features/encounters/api';
import { useRxRequestAccess } from '@/features/prescription-requests/access';
import { useNavCounts } from '@/features/shell/api';
import { PageContainer } from '@/layouts/PageContainer';
import { OutOfStockTab } from './OutOfStockTab';
import { PatientQuestionsTab } from './PatientQuestionsTab';
import { PrescriptionRequestsTab } from './PrescriptionRequestsTab';
import { ReadingAlertsTab } from './ReadingAlertsTab';

type InboxTab =
  'requests' | 'questions' | 'outOfStock' | 'results' | 'readings';

/**
 * The doctor's inbox (RL-991 permissions), one tab per kind of work. RL-1158
 * adds "Refills & outside pharmacy", RL-1139 "Questions from the app". The manager (`doctor_inbox:oversee`) opens the same page as
 * "Doctors' inbox" for the oversight view. RL-1141 adds "Reading alerts"
 * (RL-997 out-of-range home readings) for roles with `chronic_care:read`.
 */
export function DoctorInboxPage() {
  const { t } = useTranslation();
  const access = useRxRequestAccess();
  const { hasPermission } = useSession();
  const { data: counts } = useNavCounts();
  const [params, setParams] = useSearchParams();
  const items: TabItem<InboxTab>[] = [
    {
      value: 'requests',
      label: 'Refills & outside pharmacy',
      count: counts?.prescriptionRequests,
    },
    {
      value: 'questions',
      label: 'Questions from the app',
      count: counts?.patientQuestions,
    },
    // RL-1640: the dispensary's "Notify doctor" notices.
    ...(access.canView
      ? [
          {
            value: 'outOfStock' as const,
            label: t('outOfStock.tab'),
            count: counts?.outOfStockNotices,
          },
        ]
      : []),
    ...(access.canReadInbox
      ? [{ value: 'results' as const, label: 'Results to decide' }]
      : []),
    ...(hasPermission('chronic_care:read')
      ? [
          {
            value: 'readings' as const,
            label: 'Reading alerts',
            // RL-1255: open alerts (not acknowledged yet).
            count: counts?.readingAlerts,
          },
        ]
      : []),
  ];
  const asked = params.get('tab');
  const tab = items.find((i) => i.value === asked)?.value ?? 'requests';

  return (
    <PageContainer>
      <Tabs
        variant="bar"
        label="Inbox"
        value={tab}
        onChange={(next) =>
          setParams(
            (p) => {
              p.set('tab', next);
              return p;
            },
            { replace: true },
          )
        }
        items={items}
      />
      {tab === 'requests' && (
        <PrescriptionRequestsTab oversight={!access.canReadInbox} />
      )}
      {tab === 'questions' && (
        <PatientQuestionsTab oversight={!access.canReadInbox} />
      )}
      {tab === 'outOfStock' && (
        <OutOfStockTab oversight={!access.canReadInbox} />
      )}
      {tab === 'results' && <ResultsToDecide />}
      {tab === 'readings' && (
        <ReadingAlertsTab canManage={hasPermission('chronic_care:manage')} />
      )}
    </PageContainer>
  );
}

/** Results needing the doctor's decision (the inbox before RL-1158). */
function ResultsToDecide() {
  const { selected } = useBranch();
  const q = useDoctorToday(!!selected);
  const { t } = useTranslation();
  return (
    <Card title="Results needing your decision" flush>
      <DataTable
        caption="Results needing your decision"
        rows={q.data?.needsDecision}
        rowKey={(r) => `${r.labOrderId}:${r.test}`}
        loading={q.isLoading}
        error={q.error}
        onRetry={() => void q.refetch()}
        empty={
          <EmptyState
            icon={<Inbox size={22} />}
            title={
              selected
                ? t('operations.noRecords')
                : t('operations.chooseBranch')
            }
          />
        }
        columns={[
          {
            key: 'patient',
            header: 'Patient',
            render: (r) => r.patient.fullName,
          },
          { key: 'test', header: 'Test', render: (r) => r.test },
          {
            key: 'result',
            header: 'Result',
            render: (r) => [r.value, r.unit].filter(Boolean).join(' ') || '-',
          },
          { key: 'flag', header: 'Flag', render: (r) => r.flag ?? '-' },
        ]}
      />
    </Card>
  );
}
